import { createColumnHelper, flexRender } from "@tanstack/react-table";
import { useMemo } from "react";
import { fullName } from "tng-address-collection-shared/fullName.ts";
import { MySignedCardItem } from "tng-address-collection-shared/types/apiTypes.ts";
import { MemoDetailClickCell } from "../components/DetailClickCell";
import { ProjectLink } from "../components/ProjectLink";
import { SignatureCheckbox } from "../components/SignatureCheckbox";
import { useTranslator } from "../i18n/translator";
import { useUserHasActionInProjects } from "./useUserHasAction";

export const useMySignedCardsColumns = (
  handleDetailClick: (row: MySignedCardItem) => void,
) => {
  const columnHelper = useMemo(
    () => createColumnHelper<MySignedCardItem>(),
    [],
  );
  const translator = useTranslator();
  const userHasActionInProject = useUserHasActionInProjects();

  return useMemo(
    () => [
      columnHelper.display({
        id: "signature",
        header: () => translator("cardTableHeaderSigned"),
        cell: (info) => {
          const isSelected = info.row.getIsSelected();
          return flexRender(SignatureCheckbox, {
            cardId: info.row.original.id,
            cardName: fullName(info.row.original),
            projectId: info.row.original.projectId,
            isChecked: isSelected,
            onChanged: (newState) => info.row.toggleSelected(newState),
          });
        },
      }),
      columnHelper.accessor(fullName, {
        id: "name",
        header: () => translator("cardTableHeaderName"),
        cell: ({ row, getValue }) =>
          flexRender(MemoDetailClickCell, {
            handleClick: () => handleDetailClick(row.original),
            value: getValue(),
            userHasAction: userHasActionInProject(row.original.projectId),
          }),
        size: 350,
      }),
      columnHelper.accessor("projectId", {
        id: "projectId",
        header: () => translator("cardTableHeaderProject"),
        cell: (info) =>
          flexRender(ProjectLink, {
            projectId: info.row.original.projectId,
            displayName: info.row.original.projectName,
          }),
        size: 350,
      }),
      columnHelper.accessor("companyName", {
        id: "companyName",
        header: () => translator("cardTableHeaderCompanyName"),
        cell: ({ row, getValue }) =>
          flexRender(MemoDetailClickCell, {
            handleClick: () => handleDetailClick(row.original),
            value: getValue() ?? "",
            userHasAction: userHasActionInProject(row.original.projectId),
          }),
        size: 350,
      }),
      columnHelper.accessor("senderDisplayName", {
        id: "senderDisplayName",
        header: () => translator("cardTableHeaderSender"),
        cell: ({ row, getValue }) =>
          flexRender(MemoDetailClickCell, {
            handleClick: () => handleDetailClick(row.original),
            value: getValue(),
            userHasAction: userHasActionInProject(row.original.projectId),
          }),
        size: 350,
      }),
    ],
    [columnHelper, handleDetailClick, translator, userHasActionInProject],
  );
};
